import {
  BasketIcon,
  BusIcon,
  CarIcon,
  CarrotIcon,
  CheckCircleIcon,
  CheckIcon,
  CouchIcon,
  CowIcon,
  CreditCardIcon,
  DotsThreeCircleIcon,
  FirstAidKitIcon,
  ForkKnifeIcon,
  GasPumpIcon,
  GiftIcon,
  HouseIcon,
  type Icon,
  LightningIcon,
  MoneyIcon,
  PawPrintIcon,
  PiggyBankIcon,
  PillIcon,
  PopcornIcon,
  RepeatIcon,
  ScissorsIcon,
  ShieldCheckIcon,
  StudentIcon,
  TShirtIcon,
  TagIcon,
  TicketIcon,
  TrendUpIcon,
  UsersThreeIcon,
  WalletIcon,
  WifiHighIcon,
  XIcon,
} from '@phosphor-icons/react'
import { formatMoney, pd, todayIn } from '@paybae/shared'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { EmptyState } from '@/components/Card'
import { Comprobante } from '@/components/Comprobante'
import { Pastilla } from '@/components/Editorial'
import { ErrorState, Skeleton } from '@/components/Estado'
import { Guardar } from '@/components/Form'
import { AmountDisplay, NumPad } from '@/components/NumPad'
import { PersonAvatar } from '@/components/PersonAvatar'
import { ApiError, type Account, type Category, type CategoryGroup, type Transaction } from '@/lib/api'
import type { Adjunto } from '@/lib/adjuntos'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import { personPalette } from '@/lib/persons'
import { useAccounts, useCards, useCategories, useCrearGasto, useHousehold } from '@/lib/queries'
import { useSession } from '@/lib/session'

/**
 * Captura de gasto.
 *
 * Es la pantalla que decide si la app se usa o se abandona: si registrar un
 * café cuesta más que tomárselo, nadie lo registra y todos los demás números
 * quedan mintiendo. La meta es **cuatro toques y menos de ocho segundos**.
 *
 * Por eso todo vive en una sola hoja con revelación progresiva, no en un asistente
 * de pasos: se ve el avance completo de un vistazo y se puede corregir el monto
 * después de elegir la tarjeta sin perder lo andado.
 *
 * El monto va primero porque es el único dato que nadie puede adivinar; todo lo
 * demás tiene un valor por defecto razonable.
 */

/**
 * Iconos vectoriales, nunca emojis: un emoji se dibuja con la fuente del
 * sistema, así que cambia de forma entre iOS y Android, no obedece al tema y no
 * se puede alinear con precisión. Un icono de la familia Phosphor se ve igual
 * en todas partes y hereda el color del texto.
 *
 * El servidor guarda el icono como nombre (`fork-knife`), no como componente,
 * así que la traducción vive aquí. Un nombre que no esté en la tabla —una
 * categoría creada a mano más adelante— cae en la etiqueta genérica en vez de
 * dejar el hueco vacío y descuadrar la fila.
 */
const ICONOS: Record<string, Icon> = {
  house: HouseIcon,
  car: CarIcon,
  lightning: LightningIcon,
  'wifi-high': WifiHighIcon,
  repeat: RepeatIcon,
  'shield-check': ShieldCheckIcon,
  student: StudentIcon,
  basket: BasketIcon,
  carrot: CarrotIcon,
  cow: CowIcon,
  'fork-knife': ForkKnifeIcon,
  'gas-pump': GasPumpIcon,
  bus: BusIcon,
  'first-aid-kit': FirstAidKitIcon,
  pill: PillIcon,
  popcorn: PopcornIcon,
  't-shirt': TShirtIcon,
  couch: CouchIcon,
  'paw-print': PawPrintIcon,
  gift: GiftIcon,
  scissors: ScissorsIcon,
  'dots-three-circle': DotsThreeCircleIcon,
  'credit-card': CreditCardIcon,
  'piggy-bank': PiggyBankIcon,
  money: MoneyIcon,
  ticket: TicketIcon,
  'trend-up': TrendUpIcon,
}

function iconoDe(nombre: string | null): Icon {
  return (nombre ? ICONOS[nombre] : undefined) ?? TagIcon
}

/**
 * Despensa y variable primero: son las del gasto diario y las que se buscan a
 * ciegas con el pulgar. Dejar la renta o el seguro al principio obligaría a
 * recorrer la lista entera para registrar un café.
 */
const RANGO_GRUPO: Record<CategoryGroup, number> = {
  DESPENSA: 0,
  VARIABLE: 0,
  FIXED: 1,
  DEBT: 1,
  SAVINGS: 1,
  INCOME: 1,
}

type Beneficiario = { kind: 'member'; memberId: string } | { kind: 'joint' }
type MetodoPago =
  | { kind: 'account'; id: string }
  | { kind: 'voucher'; id: string }
  | { kind: 'card'; id: string }

export function CaptureSheet() {
  const navigate = useNavigate()
  const { membership } = useSession()

  const categorias = useCategories()
  const cuentas = useAccounts()
  const tarjetas = useCards()
  const hogar = useHousehold()
  const crear = useCrearGasto()

  const [amountCents, setAmountCents] = useState(0)
  const [categoria, setCategoria] = useState<Category | null>(null)
  const [beneficiario, setBeneficiario] = useState<Beneficiario>({ kind: 'joint' })
  const [metodo, setMetodo] = useState<MetodoPago | null>(null)
  /**
   * Cuándo fue el gasto.
   *
   * Estaba clavado en «hoy». Quien se acordaba por la noche del taxi de ayer
   * no tenía dónde decirlo: el gasto entraba con la fecha equivocada y caía en
   * el periodo que no era —y con una tarjeta, en el corte que no era—. La
   * única salida era editar el movimiento después, si es que alguien se daba
   * cuenta. El documento pide el campo; faltaba.
   */
  const [cuando, setCuando] = useState<'hoy' | 'ayer' | 'otro'>('hoy')
  const [fechaOtro, setFechaOtro] = useState('')
  /**
   * El movimiento ya guardado, si lo hay.
   *
   * Cuando deja de ser nulo la pantalla cambia al paso del comprobante. El
   * adjunto necesita el id del movimiento, así que **no puede** ir antes: pedir
   * la foto entre el monto y la categoría rompería la captura de ocho segundos
   * que es el objetivo entero de esta pantalla.
   */
  const [guardado, setGuardado] = useState<Transaction | null>(null)

  const categoriasOrdenadas = useMemo(
    () =>
      (categorias.data ?? [])
        .filter((c) => !c.archivedAt)
        // `sort` es estable, así que dentro de cada rango se conserva el orden
        // que ya trae el servidor (su `sortOrder`) sin tener que replicarlo.
        .sort((a, b) => RANGO_GRUPO[a.group] - RANGO_GRUPO[b.group]),
    [categorias.data],
  )

  const vales = useMemo(() => cuentasPorTipo(cuentas.data, ['MEAL_VOUCHER']), [cuentas.data])
  const liquidas = useMemo(() => cuentasPorTipo(cuentas.data, ['CHECKING', 'CASH']), [cuentas.data])
  const activas = useMemo(() => (tarjetas.data ?? []).filter((t) => !t.archivedAt), [tarjetas.data])
  const integrantes = hogar.data?.members ?? []

  /**
   * La bifurcación que pidió el cliente: los vales solo aparecen cuando la
   * categoría los admite. Ofrecerlos siempre invitaría a registrar una gasolina
   * pagada con vales — un movimiento que no existe y que descuadraría la bolsa
   * de despensa contra la realidad. El servidor también lo rechaza, pero un
   * método que no se puede usar no debería llegar a dibujarse.
   */
  const valesDisponibles = categoria?.isDespensaEligible ? vales : []

  const elegirCategoria = (next: Category): void => {
    haptic('selection')
    setCategoria(next)

    // Si ya había un método de pago con vales y la categoría nueva no los
    // admite, se limpia: dejarlo puesto guardaría un movimiento inválido.
    if (metodo?.kind === 'voucher' && !next.isDespensaEligible) {
      setMetodo(null)
    }
    // Con saldo de vales suficiente, se preselecciona: es lo que la persona
    // querría el 90% de las veces al capturar una despensa.
    if (next.isDespensaEligible && !metodo && amountCents > 0) {
      const vale = vales.find((v) => v.balanceCents >= amountCents)
      if (vale) setMetodo({ kind: 'voucher', id: vale.id })
    }
  }

  const cargando =
    categorias.isPending || cuentas.isPending || tarjetas.isPending || hogar.isPending
  const fallo = categorias.isError || cuentas.isError || tarjetas.isError || hogar.isError
  const errorCarga = categorias.error ?? cuentas.error ?? tarjetas.error ?? hogar.error

  const reintentar = (): void => {
    void categorias.refetch()
    void cuentas.refetch()
    void tarjetas.refetch()
    void hogar.refetch()
  }

  const sinMetodos = !cargando && vales.length === 0 && liquidas.length === 0 && activas.length === 0
  const completo = amountCents > 0 && categoria !== null && metodo !== null

  const hoy = todayIn(membership.household.timezone)
  const ayer = pd(restarUnDia(hoy))
  // «Otro día» solo cuenta si de verdad escribieron una fecha; si no, se
  // comporta como hoy en vez de mandar una cadena vacía al servidor.
  const fechaElegida =
    cuando === 'ayer' ? ayer : cuando === 'otro' && fechaOtro ? pd(fechaOtro) : hoy

  const guardar = (): void => {
    if (!categoria || !metodo || amountCents <= 0 || crear.isPending) return

    crear.mutate(
      {
        amountCents,
        // Fecha de calendario como texto y en el huso del hogar: un `Date` de
        // JavaScript convertido a UTC mandaría el gasto de las 9 de la noche al
        // día siguiente.
        occurredOn: fechaElegida,
        // El diseño aprobado no tiene campo de descripción, así que nunca hay
        // texto escrito y cae siempre al nombre de la categoría: un movimiento
        // sin descripción sería irreconocible después en la lista.
        description: categoria.name,
        categoryId: categoria.id,
        // Nulo = va al fondo común, que es lo que significa «Común».
        attributedToMemberId: beneficiario.kind === 'member' ? beneficiario.memberId : null,
        paymentSource: metodo.kind === 'card' ? 'CREDIT_CARD' : 'ACCOUNT',
        // Los vales son una cuenta más para el servidor; lo que los distingue
        // es su tipo, no la forma de pago.
        accountId: metodo.kind === 'card' ? undefined : metodo.id,
        creditCardId: metodo.kind === 'card' ? metodo.id : undefined,
      },
      {
        onSuccess: (transaccion) => {
          haptic('success')
          setGuardado(transaccion)
        },
        // Sin `reset` ni limpieza del formulario: volver a teclear el monto por
        // un fallo de red es la forma más rápida de que alguien abandone.
        onError: () => haptic('error'),
      },
    )
  }

  // Después de todos los hooks, nunca antes: una salida temprana colocada más
  // arriba cambiaría el número de hooks entre renders y React lo rechaza.
  if (guardado) {
    return <PasoComprobante transaccion={guardado} onListo={() => navigate(-1)} />
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg">
      {/* Durazno, el tinte de lo que sale: es la misma hoja de formulario que
          las demás (ver `FormSheet`), solo que dibujada aquí porque la captura
          vive fuera del Shell. */}
      <header className="px-safe flex items-center gap-3 bg-tint-peach pb-3 pt-safe">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Cancelar"
          className="flex size-10 items-center justify-center rounded-full text-ink-soft active:scale-95"
        >
          <XIcon size={20} />
        </button>
        <h1 className="font-display text-lg font-bold tracking-[-0.03em] text-ink">
          Registrar gasto
        </h1>
      </header>

      <div className="scroll-contain flex-1 overflow-y-auto px-4 py-4">
        {fallo ? (
          <ErrorState
            titulo="No pudimos cargar la captura"
            mensaje={
              errorCarga instanceof Error
                ? errorCarga.message
                : 'Necesitamos tus categorías y tus formas de pago para registrar el gasto.'
            }
            onReintentar={reintentar}
          />
        ) : (
          <>
            {/* Monto y teclado arriba y juntos: es el primer dato que se captura y
                el único que nadie puede adivinar. Ponerlos al final obligaría a
                desplazarse antes de teclear la primera cifra, que es justo lo que
                hace que registrar un gasto se sienta lento.

                Se dibujan también mientras cargan los catálogos porque no dependen
                de la red: así el monto ya está escrito cuando llegan las categorías. */}
            <AmountDisplay cents={amountCents} />
            <NumPad valueCents={amountCents} onChange={setAmountCents} />

            {cargando ? (
              <Cargando />
            ) : (
              <>
                <Bloque titulo="¿En qué?">
                  {categoriasOrdenadas.length === 0 ? (
                    <EmptyState
                      title="Todavía no hay categorías"
                      description="Sin categorías no se puede clasificar el gasto. Crea la primera desde Ajustes y vuelve aquí."
                      action={<BotonIrAAjustes onClick={() => navigate('/ajustes')} />}
                      icon={<TagIcon size={26} />}
                    />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {categoriasOrdenadas.map((c) => {
                        const activa = categoria?.id === c.id
                        const CategoriaIcon = iconoDe(c.icon)
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => elegirCategoria(c)}
                            aria-pressed={activa}
                            className={cn(
                              'flex min-h-[44px] items-center gap-2 rounded-pill border px-3.5 py-2 text-sm font-medium',
                              'transition-colors duration-150 active:scale-95',
                              activa
                                ? 'border-accion bg-accion text-on-accion'
                                : 'border-line bg-surface text-ink-soft',
                            )}
                          >
                            <CategoriaIcon size={17} weight={activa ? 'fill' : 'regular'} />
                            {c.name}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </Bloque>

                <Bloque titulo="¿De quién es?">
                  <div className="flex gap-2">
                    <BotonBeneficiario
                      activo={beneficiario.kind === 'joint'}
                      onClick={() => {
                        haptic('selection')
                        setBeneficiario({ kind: 'joint' })
                      }}
                    >
                      <span className="flex size-9 items-center justify-center rounded-full bg-surface-sunken text-ink-soft">
                        <UsersThreeIcon size={18} weight="fill" />
                      </span>
                      Común
                    </BotonBeneficiario>

                    {integrantes.map((m) => (
                      <BotonBeneficiario
                        key={m.id}
                        activo={beneficiario.kind === 'member' && beneficiario.memberId === m.id}
                        onClick={() => {
                          haptic('selection')
                          setBeneficiario({ kind: 'member', memberId: m.id })
                        }}
                      >
                        {/* `personColorToken`, nunca `m.id`: los dos son cadenas y
                            el compilador no distingue uno del otro, pero pasar el
                            id dejaría a todo el hogar del mismo color. */}
                        <PersonAvatar name={m.user.displayName} token={m.personColorToken} size="md" />
                        {m.user.displayName.split(' ')[0]}
                      </BotonBeneficiario>
                    ))}
                  </div>
                </Bloque>

                <Bloque titulo="¿Cuándo fue?">
                  <SelectorCuando
                    valor={cuando}
                    onCambio={setCuando}
                    fechaOtro={fechaOtro}
                    onFechaOtro={setFechaOtro}
                    hoy={hoy}
                  />
                </Bloque>

                <Bloque titulo="¿Con qué se paga?">
                  {sinMetodos ? (
                    <EmptyState
                      title="Falta con qué pagar"
                      description="Agrega tu cuenta de nómina o una tarjeta y podrás registrar gastos en cuatro toques."
                      action={<BotonIrAAjustes onClick={() => navigate('/ajustes')} />}
                      icon={<WalletIcon size={26} />}
                    />
                  ) : (
                    <div className="flex flex-col gap-2">
                      {valesDisponibles.length > 0 ? (
                        <>
                          <Etiqueta>Vales de despensa</Etiqueta>
                          {valesDisponibles.map((v) => {
                            const insuficiente = amountCents > v.balanceCents
                            return (
                              <FilaMetodo
                                key={v.id}
                                activo={metodo?.kind === 'voucher' && metodo.id === v.id}
                                onClick={() => {
                                  haptic('selection')
                                  setMetodo({ kind: 'voucher', id: v.id })
                                }}
                                icono={<BasketIcon size={18} weight="fill" />}
                                titulo={v.name}
                                detalle={
                                  insuficiente
                                    ? `Saldo insuficiente · ${formatMoney(v.balanceCents, { hideZeroCents: true })}`
                                    : `Saldo ${formatMoney(v.balanceCents, { hideZeroCents: true })}`
                                }
                                alerta={insuficiente}
                                colorToken={v.owner?.personColorToken ?? null}
                              />
                            )
                          })}
                        </>
                      ) : null}

                      {liquidas.length > 0 ? (
                        <>
                          <Etiqueta>Efectivo y débito</Etiqueta>
                          {liquidas.map((c) => {
                            const insuficiente = amountCents > c.balanceCents
                            return (
                              <FilaMetodo
                                key={c.id}
                                activo={metodo?.kind === 'account' && metodo.id === c.id}
                                onClick={() => {
                                  haptic('selection')
                                  setMetodo({ kind: 'account', id: c.id })
                                }}
                                icono={<WalletIcon size={18} weight="fill" />}
                                titulo={c.name}
                                detalle={
                                  insuficiente
                                    ? `Saldo insuficiente · ${formatMoney(c.balanceCents, { hideZeroCents: true })}`
                                    : `Saldo ${formatMoney(c.balanceCents, { hideZeroCents: true })}`
                                }
                                alerta={insuficiente}
                                colorToken={c.owner?.personColorToken ?? null}
                              />
                            )
                          })}
                        </>
                      ) : null}

                      {activas.length > 0 ? (
                        <>
                          <Etiqueta>Tarjetas de crédito</Etiqueta>
                          {activas.map((card) => (
                            <FilaMetodo
                              key={card.id}
                              activo={metodo?.kind === 'card' && metodo.id === card.id}
                              onClick={() => {
                                haptic('selection')
                                setMetodo({ kind: 'card', id: card.id })
                              }}
                              icono={<CreditCardIcon size={18} weight="fill" />}
                              titulo={`${card.name}${card.last4 ? ` ·· ${card.last4}` : ''}`}
                              // El crédito disponible lo calcula el servidor y se
                              // muestra al elegir, no después: es justo el momento
                              // en que sirve para decidir.
                              detalle={`Disponible ${formatMoney(card.availableCreditCents, { hideZeroCents: true })}`}
                              colorToken={card.owner?.personColorToken ?? null}
                            />
                          ))}
                        </>
                      ) : null}
                    </div>
                  )}
                </Bloque>
              </>
            )}

            <div className="h-6" />
          </>
        )}
      </div>

      <footer className="border-t border-line bg-surface px-4 pb-safe pt-3">
        {crear.isError ? (
          // El error se queda a la vista y el formulario intacto: el botón sigue
          // disponible y el segundo intento no cuesta ni un toque más.
          <p
            role="alert"
            className="mb-2.5 rounded-control bg-expense-soft px-3 py-2.5 text-[13px] text-expense-text"
          >
            {mensajeDeError(crear.error)}
          </p>
        ) : null}

        {/* El mismo botón que el resto de formularios. Era una copia a mano de
            `Guardar` con radio de tarjeta y sin chevrón: la captura, que es la
            acción más repetida de la app, era la única que no tenía la forma
            del botón del sistema. */}
        <Guardar onClick={guardar} disabled={!completo} guardando={crear.isPending}>
          {completo ? 'Guardar gasto' : siguientePaso(amountCents, categoria, metodo)}
        </Guardar>
      </footer>
    </div>
  )
}

/**
 * Paso opcional: el comprobante (§23).
 *
 * Va **después** de guardar y no antes por dos razones que apuntan al mismo
 * sitio. La técnica: el adjunto cuelga del id del movimiento, y ese id no
 * existe hasta que el servidor responde. La de diseño, que pesa más: el
 * objetivo de esta pantalla son cuatro toques y ocho segundos, y un paso de
 * cámara en medio lo dobla. Aquí el gasto ya está anotado —lo dice la pantalla
 * antes que nada—, así que cerrar sin adjuntar nada no pierde absolutamente
 * nada.
 */
function PasoComprobante({
  transaccion,
  onListo,
}: {
  transaccion: Transaction
  onListo: () => void
}) {
  const [adjuntos, setAdjuntos] = useState<Adjunto[]>([])

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg">
      {/* Durazno, el tinte de lo que sale: es la misma hoja de formulario que
          las demás (ver `FormSheet`), solo que dibujada aquí porque la captura
          vive fuera del Shell. */}
      <header className="px-safe flex items-center gap-3 bg-tint-peach pb-3 pt-safe">
        <button
          type="button"
          onClick={() => {
            haptic('light')
            onListo()
          }}
          aria-label="Terminar"
          className="flex size-10 items-center justify-center rounded-full text-ink-soft active:scale-95"
        >
          <XIcon size={20} />
        </button>
        <h1 className="font-display text-lg font-bold tracking-[-0.03em] text-ink">
          Gasto registrado
        </h1>
      </header>

      <div className="scroll-contain flex-1 overflow-y-auto px-4 py-4">
        <div className="mx-auto flex max-w-lg flex-col gap-5">
          {/* La confirmación en tinta y con el índigo de marca, no en verde: el
              menta significa «entró dinero» en toda la app, y usarlo para
              celebrar un gasto lo diría al revés. */}
          <div className="flex items-center gap-3 rounded-card border border-line bg-surface p-3.5">
            <CheckCircleIcon size={26} weight="fill" className="shrink-0 text-primary" />
            <p className="min-w-0 flex-1 text-[14px] leading-snug text-ink">
              <strong data-money className="font-semibold">
                {formatMoney(transaccion.amountCents, { hideZeroCents: true })}
              </strong>{' '}
              en {transaccion.description} — ya quedó anotado.
            </p>
          </div>

          <section className="flex flex-col gap-2.5">
            <h2 className="text-[15px] font-semibold text-ink">¿Tienes el ticket a la mano?</h2>
            <p className="text-[13px] leading-snug text-ink-soft">
              Guardarlo ahora te ahorra buscarlo cuando toque aclarar el cargo o hacer una
              devolución. Si no lo tienes, no pasa nada: el gasto ya está registrado.
            </p>

            <Comprobante
              destino={{ transactionId: transaccion.id }}
              adjuntos={adjuntos}
              onSubido={(adjunto) => setAdjuntos((previos) => [...previos, adjunto])}
              onBorrado={(id) => setAdjuntos((previos) => previos.filter((a) => a.id !== id))}
            />
          </section>
        </div>
      </div>

      <footer className="border-t border-line bg-surface px-4 pb-safe pt-3">
        <Pastilla onClick={onListo} icono={<CheckIcon size={19} weight="bold" />}>
          {adjuntos.length > 0 ? 'Listo' : 'Terminar sin comprobante'}
        </Pastilla>
      </footer>
    </div>
  )
}

/**
 * Esqueleto de los tres bloques.
 *
 * Los títulos y las etiquetas no dependen de la red, así que se dibujan de
 * verdad y solo se sustituyen las filas: el contenido real aterriza exactamente
 * donde estaba el esqueleto y la pantalla no da el salto que haría tocar lo que
 * no era.
 */
function Cargando() {
  return (
    <>
      <Bloque titulo="¿En qué?">
        <div className="flex flex-wrap gap-2">
          {ANCHOS_CHIP.map((ancho, i) => (
            <Skeleton key={i} className={cn('h-11 rounded-pill', ancho)} />
          ))}
        </div>
      </Bloque>

      <Bloque titulo="¿De quién es?">
        <div className="flex gap-2">
          <Skeleton className="h-[54px] flex-1" />
          <Skeleton className="h-[54px] flex-1" />
        </div>
      </Bloque>

      <Bloque titulo="¿Con qué se paga?">
        <div className="flex flex-col gap-2">
          <Etiqueta>Efectivo y débito</Etiqueta>
          <Skeleton className="h-[58px]" />
          <Skeleton className="h-[58px]" />
          <Etiqueta>Tarjetas de crédito</Etiqueta>
          <Skeleton className="h-[58px]" />
          <Skeleton className="h-[58px]" />
        </div>
      </Bloque>
    </>
  )
}

/**
 * Anchos escritos a mano y no calculados: Tailwind lee las clases del código
 * fuente, así que una clase armada con plantilla nunca llegaría al CSS final.
 * Son irregulares a propósito, como los nombres de categoría que sustituyen.
 */
const ANCHOS_CHIP = [
  'w-[116px]',
  'w-[136px]',
  'w-[104px]',
  'w-[124px]',
  'w-[96px]',
  'w-[112px]',
  'w-[92px]',
  'w-[128px]',
] as const

function BotonIrAAjustes({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={() => {
        haptic('light')
        onClick()
      }}
      className="flex min-h-[44px] items-center rounded-pill bg-accion px-5 text-[14px] font-semibold text-on-accion active:scale-95"
    >
      Ir a Ajustes
    </button>
  )
}

/** Cuentas vivas de los tipos pedidos, en el orden que manda el servidor. */
function cuentasPorTipo(
  data: Account[] | undefined,
  tipos: readonly Account['type'][],
): Account[] {
  return (data ?? []).filter((c) => !c.archivedAt && tipos.includes(c.type))
}

/**
 * Un «Revisa los datos del formulario» sin más no dice qué corregir; el detalle
 * por campo que manda el servidor sí.
 */
function mensajeDeError(error: unknown): string {
  if (error instanceof ApiError) {
    const detalles = Object.values(error.fieldErrors ?? {}).flat()
    return detalles.length > 0 ? detalles.join('. ') : error.message
  }
  return 'No pudimos guardar el gasto. Inténtalo de nuevo.'
}

/** Dice qué falta, en vez de un botón muerto sin explicación. */
function siguientePaso(
  amountCents: number,
  categoria: Category | null,
  metodo: MetodoPago | null,
): string {
  if (amountCents === 0) return 'Escribe el monto'
  if (!categoria) return 'Elige una categoría'
  if (!metodo) return 'Elige cómo se paga'
  return 'Guardar gasto'
}

function Bloque({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="mt-5">
      <h2 className="mb-2.5 text-[13px] font-semibold text-ink">{titulo}</h2>
      {children}
    </section>
  )
}

function Etiqueta({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.06em] text-ink-faint">
      {children}
    </p>
  )
}

function BotonBeneficiario({
  activo,
  onClick,
  children,
}: {
  activo: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activo}
      className={cn(
        'flex min-h-[44px] flex-1 items-center gap-2 rounded-card border px-3 py-2 text-sm font-medium',
        'transition-colors duration-150 active:scale-95',
        activo ? 'border-primary bg-primary-soft text-ink' : 'border-line bg-surface text-ink-soft',
      )}
    >
      {children}
    </button>
  )
}

function FilaMetodo({
  activo,
  onClick,
  icono,
  titulo,
  detalle,
  colorToken,
  alerta = false,
}: {
  activo: boolean
  onClick: () => void
  icono: React.ReactNode
  titulo: string
  detalle: string
  /** Token de color de identidad del dueño, no su id. */
  colorToken?: string | null
  alerta?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activo}
      className={cn(
        'flex min-h-[52px] w-full items-center gap-3 rounded-card border px-3 py-2.5 text-left',
        'transition-colors duration-150 active:scale-[0.99]',
        activo ? 'border-primary bg-primary-soft' : 'border-line bg-surface',
      )}
    >
      <span className={cn('shrink-0', colorToken ? personPalette(colorToken).text : 'text-ink-faint')}>
        {icono}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-ink">{titulo}</span>
        <span className={cn('block truncate text-xs', alerta ? 'text-expense-text' : 'text-ink-soft')}>
          {detalle}
        </span>
      </span>
      {activo ? <CheckIcon size={18} weight="bold" className="shrink-0 text-ink" /> : null}
    </button>
  )
}


/**
 * Cuándo ocurrió el gasto.
 *
 * Tres botones y no un calendario: el 95 % de las capturas son de hoy o de
 * ayer, y obligar a abrir un selector de fechas para el caso común es el tipo
 * de fricción que hace que la gente deje de registrar. «Otro día» abre el
 * campo nativo, que en el teléfono es la rueda del sistema.
 *
 * Importa más de lo que parece con tarjeta de crédito: la fecha decide en qué
 * corte cae la compra, y por tanto cuándo hay que pagarla.
 */
function SelectorCuando({
  valor,
  onCambio,
  fechaOtro,
  onFechaOtro,
  hoy,
}: {
  valor: 'hoy' | 'ayer' | 'otro'
  onCambio: (v: 'hoy' | 'ayer' | 'otro') => void
  fechaOtro: string
  onFechaOtro: (v: string) => void
  hoy: string
}) {
  const opciones = [
    { v: 'hoy' as const, label: 'Hoy' },
    { v: 'ayer' as const, label: 'Ayer' },
    { v: 'otro' as const, label: 'Otro día' },
  ]

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        {opciones.map((o) => {
          const activo = valor === o.v
          return (
            <button
              key={o.v}
              type="button"
              aria-pressed={activo}
              onClick={() => {
                haptic('selection')
                onCambio(o.v)
              }}
              className={cn(
                'min-h-[44px] flex-1 rounded-pill text-[14px] font-semibold transition-colors active:scale-95',
                activo
                  ? 'bg-ink text-[var(--papel,#faf8f4)]'
                  : 'border border-line bg-surface text-ink-soft',
              )}
            >
              {o.label}
            </button>
          )
        })}
      </div>
      {valor === 'otro' ? (
        <input
          type="date"
          value={fechaOtro}
          // Nadie registra un gasto del futuro: el tope evita el dedazo que
          // mandaría el movimiento a un periodo que todavía no existe.
          max={hoy}
          onChange={(e) => onFechaOtro(e.target.value)}
          className="h-[52px] w-full rounded-card border border-transparent bg-surface-sunken px-3.5 text-[16px] text-ink outline-none focus:border-primary focus:bg-surface"
        />
      ) : null}
    </div>
  )
}

/**
 * El día anterior, sobre la fecha de calendario en texto.
 *
 * Con `new Date()` habría que pelear con el huso: a las 9 de la noche en
 * México, el UTC ya es el día siguiente y «ayer» saldría siendo hoy. Restar
 * sobre las tres cifras no tiene ese problema.
 */
function restarUnDia(fecha: string): string {
  const [a, m, d] = fecha.split('-').map(Number)
  const t = Date.UTC(a as number, (m as number) - 1, (d as number) - 1)
  const previo = new Date(t)
  const mm = String(previo.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(previo.getUTCDate()).padStart(2, '0')
  return `${previo.getUTCFullYear()}-${mm}-${dd}`
}
